import React, { useRef, useEffect } from "react";
import type { UsedSheet } from "./types.ts";
import { t, type Lang } from "./i18n.ts";

// Deterministic color palette for pieces
const COLORS = [
  "#3b82f6", "#ef4444", "#22c55e", "#f59e0b", "#8b5cf6",
  "#ec4899", "#06b6d4", "#f97316", "#14b8a6", "#6366f1",
  "#d946ef", "#84cc16", "#e11d48", "#0ea5e9", "#a855f7",
  "#10b981", "#f43f5e", "#7c3aed", "#eab308", "#2563eb",
];

interface Props {
  sheet: UsedSheet;
  lang: Lang;
  fullscreen?: boolean;
}

export function SheetCanvas({ sheet, lang, fullscreen }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let maxW: number;
    let maxH: number;

    if (fullscreen) {
      // Use window dimensions minus chrome (top bar ~52px, bottom bar ~52px, padding)
      maxW = window.innerWidth - 80;
      maxH = window.innerHeight - 160;
    } else {
      maxW = 800;
      maxH = 400;
    }

    const scale = Math.min(maxW / sheet.width, maxH / sheet.height);
    const cw = Math.round(sheet.width * scale);
    const ch = Math.round(sheet.height * scale);

    // HiDPI
    const dpr = window.devicePixelRatio || 1;
    canvas.width = cw * dpr;
    canvas.height = ch * dpr;
    canvas.style.width = `${cw}px`;
    canvas.style.height = `${ch}px`;
    ctx.scale(dpr, dpr);

    // Background (waste area)
    ctx.fillStyle = "#1a1a2e";
    ctx.fillRect(0, 0, cw, ch);

    // Grid lines
    ctx.strokeStyle = "#ffffff08";
    ctx.lineWidth = 0.5;
    const gridStep = 100; // mm
    for (let x = gridStep; x < sheet.width; x += gridStep) {
      ctx.beginPath();
      ctx.moveTo(x * scale, 0);
      ctx.lineTo(x * scale, ch);
      ctx.stroke();
    }
    for (let y = gridStep; y < sheet.height; y += gridStep) {
      ctx.beginPath();
      ctx.moveTo(0, y * scale);
      ctx.lineTo(cw, y * scale);
      ctx.stroke();
    }

    // Draw pieces
    for (const p of sheet.placements) {
      const x = p.x * scale;
      const y = p.y * scale;
      const w = p.width * scale;
      const h = p.height * scale;
      const color = COLORS[p.piece_index % COLORS.length]!;

      // Fill
      ctx.fillStyle = color + "cc";
      ctx.fillRect(x, y, w, h);

      // Border
      ctx.strokeStyle = color;
      ctx.lineWidth = fullscreen ? 2.5 : 1.5;
      ctx.strokeRect(x, y, w, h);

      // Label text
      const label = p.label || `${p.width}×${p.height}`;
      const dimText = `${p.width}×${p.height}`;
      const fontSize = fullscreen
        ? Math.min(28, Math.max(12, Math.min(w, h) * 0.18))
        : Math.min(14, Math.max(8, Math.min(w, h) * 0.15));

      ctx.fillStyle = "#fff";
      ctx.font = `bold ${fontSize}px 'Inter', system-ui, sans-serif`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      const cx = x + w / 2;
      const cy = y + h / 2;

      // Draw label
      if (w > 30 && h > 20) {
        ctx.fillText(label, cx, cy - fontSize * 0.6, w - 4);
        ctx.font = `${fontSize * 0.8}px 'Inter', system-ui, sans-serif`;
        ctx.fillText(dimText, cx, cy + fontSize * 0.5, w - 4);
        if (p.rotated) {
          ctx.font = `${fontSize * 0.7}px 'Inter', system-ui, sans-serif`;
          ctx.fillStyle = "#ffffffaa";
          ctx.fillText(`↻ ${t("rotated", lang)}`, cx, cy + fontSize * 1.3, w - 4);
        }
      }
    }

    // Draw remnants (usable leftover areas) as dashed green rectangles
    for (const r of sheet.remnants) {
      const rx = r.x * scale;
      const ry = r.y * scale;
      const rw = r.width * scale;
      const rh = r.height * scale;

      // Green tint fill
      ctx.fillStyle = "#22c55e22";
      ctx.fillRect(rx, ry, rw, rh);

      // Dashed green border
      ctx.strokeStyle = "#22c55e";
      ctx.lineWidth = fullscreen ? 2.5 : 1.5;
      ctx.setLineDash([6, 4]);
      ctx.strokeRect(rx, ry, rw, rh);
      ctx.setLineDash([]);

      // Dimensions label
      const fontSize = fullscreen
        ? Math.min(22, Math.max(10, Math.min(rw, rh) * 0.15))
        : Math.min(11, Math.max(7, Math.min(rw, rh) * 0.12));
      if (rw > 40 && rh > 20) {
        ctx.fillStyle = "#22c55ecc";
        ctx.font = `${fontSize}px 'Inter', system-ui, sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(
          `${Math.round(r.width)}×${Math.round(r.height)}`,
          rx + rw / 2,
          ry + rh / 2,
          rw - 4
        );
      }
    }

    // Sheet border
    ctx.strokeStyle = "#475569";
    ctx.lineWidth = fullscreen ? 3 : 2;
    ctx.strokeRect(0, 0, cw, ch);

    // Dimension labels on edges
    const dimFontSize = fullscreen ? 16 : 11;
    ctx.fillStyle = "#94a3b8";
    ctx.font = `${dimFontSize}px 'Inter', system-ui, sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText(`${sheet.width} mm`, cw / 2, ch + 4);
    ctx.save();
    ctx.translate(-4, ch / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.textBaseline = "bottom";
    ctx.fillText(`${sheet.height} mm`, 0, 0);
    ctx.restore();
  }, [sheet, lang, fullscreen]);

  if (fullscreen) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <canvas ref={canvasRef} />
      </div>
    );
  }

  return (
    <div className="overflow-x-auto pb-4">
      <canvas
        ref={canvasRef}
        className="border rounded"
        style={{ maxWidth: "100%" }}
      />
      {/* Piece legend */}
      <div className="flex flex-wrap gap-2 mt-3">
        {sheet.placements.map((p, i) => (
          <div key={i} className="flex items-center gap-1 text-xs">
            <div
              className="w-3 h-3 rounded-sm"
              style={{
                backgroundColor: COLORS[p.piece_index % COLORS.length],
              }}
            />
            <span>
              {p.label || "?"} {p.width}×{p.height}
              {p.rotated ? ` (${t("rotated", lang)})` : ""}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
